import { mapState } from 'vuex';

import {
  createTicker, generateRandom2Digits, genGUID, wrapToLoadingFn,
} from '@helpers';
import { APP_LOCATIONS_NAMESPACE } from '@store';
import { generateReplacementStringFromObj } from '@modules/settings/replacements';

import { DashboardApiService } from '../../_services/dashboard.api.service';
import { generateQueues, generateGroups, sortQueue } from '../../_helpers/queuesAndGroupsGenerators';
import { createPartiallyRenderer } from '../../_helpers/partiallyRender';
import { createMemoWrapper } from '../../_helpers/dashboardMemoization';
import { createDashboardModel } from '../../_models/dashboard';
import { calculateTotalTime } from '../../_helpers/time';
import PbProxyNotifications from '../../_components/PbProxyNotifications';

import PbDashboardJobsCsvDownloader from '../../_components/PbDashboardJobsCsvDownloader.vue';
import PbDashboardTotalTimeAndJobs from '../../_components/PbDashboardTotalTimeAndJobs.vue';
import PbDashboardToQueueSender from '../../_components/PbDashboardToQueueSender.vue';
import PbDashboardJobsSearch from '../../_components/PbDashboardJobsSearch';
import PbDashboardJobsViewer from '../../_components/PbDashboardJobsViewer';
import PbDashboardFilters from '../../_components/PbDashboardFilters.vue';
import PbDashboardGroups from '../../_components/PbDashboardGroups.vue';
import PbDashboardQueues from '../../_components/PbDashboardQueues';

import PbDashboardCard, { CARD_STATES } from '../../_components/PbDashboardCard';

const DashboardNotStartedMemo = createMemoWrapper({ prepareDataHandler: sortQueue });
const DashboardInProgressMemo = createMemoWrapper();
const DashboardGroupsMemo = createMemoWrapper();
const DashboardTicker = createTicker();

export default {
  name: 'PbDashboard',

  components: {
    PbDashboardJobsCsvDownloader,
    PbDashboardTotalTimeAndJobs,
    PbDashboardToQueueSender,
    PbDashboardJobsSearch,
    PbDashboardJobsViewer,
    PbDashboardFilters,
    PbDashboardGroups,
    PbDashboardQueues,
    PbDashboardCard,
    PbProxyNotifications,
  },

  data() {
    return {
      CARD_STATES,

      renderId: '',

      machineCategory: '',
      machineCategories: [],

      profitFilters: [],
      shippingPeriodsFilters: [],

      groups: {},
      printMachines: [],
      queues: { inProgress: {}, notStarted: {} },
      dashboardItems: createDashboardModel(),

      highlightedFoundJobsIds: [],

      scrollContainerHeight: 0,

      isGroupsMode: true,
      isVisibleJobsSearch: false,
      dashboardItemsLoading: false,

      jobsSearchingLoading: true,
      dashboardModeSwitchLoading: true,

      toQueueSenderDialog: false,
      toQueueSenderData: {
        substrate: '',
        machine: '',
        filters: {},
        item: {},
        minWidth: 0,
        minHeight: 0,
      },

      proxyNotifications: [],
    };
  },

  computed: {
    ...mapState(APP_LOCATIONS_NAMESPACE, ['suffix']),

    totalEstimatedTime() {
      const { groups, queue, baskets } = this.dashboardItems;

      return {
        // concat queue and baskets, because it's 2 parts of printing queue
        queue: calculateTotalTime({ items: [...queue, ...baskets] }),
        groups: calculateTotalTime({ items: groups }),
      };
    },

    totalJobsCount() {
      const { groups, queue, baskets } = this.dashboardItems;

      const queueCount = [...queue, ...baskets].reduce((acc, { count }) => acc + count, 0);
      const groupsCount = groups.reduce((acc, { count }) => acc + count, 0);

      return {
        total: groupsCount + queueCount,
        groups: groupsCount,
        queue: queueCount,
      };
    },

    // Items count for using in filters components
    dashboardItemsCountByFilters() {
      return [
        ...(this.dashboardItems.shippingPeriodsQuantities || []),
        ...(this.dashboardItems.filtersQuantities || []),
      ]
        .reduce((acc, { period, filter, quantity }) => {
          acc[period || filter] = (acc[period || filter] || 0) + quantity;
          return acc;
        }, {});
    },
  },

  watch: {
    suffix: {
      handler(suffix) {
        if (suffix) {
          this.resetGroups();
          this.resetQueues();
          this.clearDashboardMemoization();
          this.initDashboard({ suffix });
        }
      },
      immediate: true,
    },

    machineCategory(machineCategory) {
      if (machineCategory) {
        this.resetDashboardOptions();
        this.clearDashboardMemoization();
        this.refreshDashboardQueue({ rerender: true, withLoadingSpinner: true, machineCategory });
      }
    },
  },

  created() {
    window.addEventListener('resize', this.updateScrollContainerHeight);
    this.updateScrollContainerHeight();
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.updateScrollContainerHeight);
    this.destroyDashboardMemoization();
    DashboardTicker.destroyTicker();
  },

  methods: {
    // @TODO make better loading flags control
    async getDashboardItems({
      suffix = this.suffix,
      rerender = false,
      withLoadingSpinner = false,
      jobs = this.highlightedFoundJobsIds,
      machineCategory = this.machineCategory,
      // groupFilter in api call, semantically it's profit filter
      profitFilters: groupFilter = this.profitFilters,
      shippingPeriodsFilters = this.shippingPeriodsFilters,
    } = {}) {
      if (rerender) {
        this.jobsSearchingLoading = true;
        this.dashboardModeSwitchLoading = true;
      }
      if (withLoadingSpinner) {
        this.dashboardItemsLoading = true;
      }

      return this.wrapToLoadingFn({
        req: DashboardApiService.getDashboardItems.bind(
          {},
          {
            jobs,
            suffix,
            machineCategory,
            groupFilter: groupFilter.length ? groupFilter : null,
            // Convert to string because backend uses asp net bitmap for this param
            shippingPeriods: shippingPeriodsFilters.join(',') || null,
          },
        ),

        onSuccess: async (data) => {
          if (withLoadingSpinner) {
            this.dashboardItemsLoading = false;
          }

          this.$set(this, 'dashboardItems', data);
          this.$set(this, 'proxyNotifications', data.proxyNotifications || []);

          const machines = data.machines || [];

          if (
            (machines.length !== this.printMachines.length)
            || !machines.every(m => this.printMachines.includes(m))
          ) {
            this.$set(this, 'printMachines', machines);
          }

          const visibleLayerRender = this[this.isGroupsMode ? 'updateGroups' : 'updateQueues']({ rerender });
          const invisibleLayerRender = this[this.isGroupsMode ? 'updateQueues' : 'updateGroups']({ rerender, isFirstFullLayer: false });

          if (rerender) {
            this.renderId = genGUID();

            const visibleLayer = async () => {
              await Promise.all(visibleLayerRender.map(r => this.renderDashboardPartially({
                ...r,
                renderId: this.renderId,
              })));

              this.jobsSearchingLoading = false;
            };

            const invisibleLayer = () => new Promise(resolve => setTimeout(
              async () => {
                await Promise.all(invisibleLayerRender.map(r => this.renderDashboardPartially({
                  ...r,
                  renderId: this.renderId,
                })));

                resolve();
              },
              500,
            ));

            Promise.all([visibleLayer(), invisibleLayer()]).then(() => {
              this.dashboardModeSwitchLoading = false;
            });
          }
        },

        onReject: (e) => {
          if (e.isCancel) {
            return;
          }

          this.$VBlackerTheme.notification.error(e.msg);

          if (withLoadingSpinner) {
            this.dashboardItemsLoading = false;
          }

          if (rerender) {
            this.jobsSearchingLoading = false;
            this.dashboardModeSwitchLoading = false;
          }
        },
      });
    },

    updateGroups({ rerender = false, isFirstFullLayer = true }) {
      // These keys are indicator of groups items and may be used as identifier;
      // All groups generates 'on air' in backend
      const groupKeyKeys = [
        'substrate', 'color', 'quantity', 'count', 'width', 'height', 'selected',
      ];
      const generateGroupItemKey = item => groupKeyKeys
        .reduce((acc, key) => `${acc}${acc ? '-' : ''}${item[key]}`, '');

      const groups = generateGroups(this.dashboardItems);
      const prevGroupsKeys = Object.keys(this.groups);
      const nextGroupsKeys = Object.keys(groups);

      // Check for new render items for disable not used rerender
      prevGroupsKeys.forEach((key) => {
        if (!nextGroupsKeys.includes(key)) {
          this.$delete(this.groups, key);
        }
      });

      nextGroupsKeys.forEach((key) => {
        const refresh = DashboardGroupsMemo.addItems(key, groups[key], {
          memoKeyGenerator: generateGroupItemKey,
        }) || !this.groups[key];

        if (refresh && !rerender) {
          this.$set(this.groups, key, DashboardGroupsMemo.getItemsByName(key));
        }
      });

      return [{
        memo: DashboardGroupsMemo,
        target: this.groups,
        isFirstFullLayer,
      }];
    },

    updateQueues({ rerender = false, isFirstFullLayer = true }) {
      const { dashboardItems } = this;

      const queues = {
        inProgress: generateQueues({ dashboardItems, queuesType: 'baskets' }),
        notStarted: generateQueues({ dashboardItems, queuesType: 'queue' }),
      };

      /* eslint-disable no-underscore-dangle */
      Object.keys(queues.notStarted).forEach((key) => {
        sortQueue(queues.notStarted[key]).forEach((item, i) => {
          item._withMoves = queues.notStarted[key].length > 1;

          if (item._withMoves) {
            item._isFirst = i === 0;
            item._isLast = i === queues.notStarted[key].length - 1;
          }
        });
      });
      /* eslint-enable no-underscore-dangle */

      const queueKeyKeys = [
        'id', 'machine', 'number', 'selected', 'state',
        'printedQuantity', '_isFirst', '_isLast', '_withMoves',
      ];
      const generateQueueItemKey = item => queueKeyKeys
        .reduce((acc, key) => `${acc}${acc ? '-' : ''}${item[key]}`, '');

      const refreshQueue = (queue, queueType, memo) => Object.keys(queue).forEach((key) => {
        const refresh = memo.addItems(key, queue[key], {
          memoKeyGenerator: generateQueueItemKey,
        }) || !this.queues[queueType][key];

        if (refresh && !rerender) {
          this.$set(this.queues[queueType], key, memo.getItemsByName(key));
        }
      });

      refreshQueue(queues.inProgress, 'inProgress', DashboardInProgressMemo);
      refreshQueue(queues.notStarted, 'notStarted', DashboardNotStartedMemo);

      return [
        {
          target: this.queues.inProgress,
          memo: DashboardInProgressMemo,
          isFirstFullLayer,
        },
        {
          target: this.queues.notStarted,
          memo: DashboardNotStartedMemo,
          isFirstFullLayer,
        },
      ];
    },

    renderDashboardPartially({
      isFirstFullLayer,
      renderId,
      target,
      memo,
    }) {
      return new Promise((resolve) => {
        const Render = createPartiallyRenderer({
          getNextRenderId: () => this.renderId,
          isFirstFullLayer,
          renderId,
        });

        const renderTree = Object.keys(memo.getAllItems())
          .map(key => [key, memo.getItemsByName(key)]);

        Render.runRenderLoop({
          onFirstLayerRender: l => Object.keys(l).forEach(key => this.$set(target, key, l[key])),
          onLayerRender: ({ nodeKey, currentNode }) => {
            if (renderId !== this.renderId) {
              return;
            }

            if (!target[nodeKey]) {
              this.$set(target, nodeKey, []);
            }

            target[nodeKey].push(...currentNode);
          },
          onRenderingFinal: resolve,
          tree: renderTree,
        });
      });
    },

    getMachineCategories({ suffix = this.suffix } = {}) {
      return this.wrapToLoadingFn({
        req: DashboardApiService.getMachineCategories.bind({}, { suffix }),
        onSuccess: (data) => {
          this.machineCategories = data
            .map(category => ({
              key: category,
              text: category,
              groupsId: `dash-groups-${category}`,
              queuesId: `dash-queues-${category}`,
            }))
            .sort(({ key: a }, { key: b }) => a.localeCompare(b));

          this.machineCategory = '';

          if (!data.length) {
            return;
          }

          setTimeout(() => {
            this.machineCategory = this.machineCategories[0].key;
          }, 0);
        },
        onReject: ({ msg }) => {
          this.$VBlackerTheme.notification.error(msg);
          this.dashboardItemsLoading = false;
        },
      });
    },

    async refreshDashboardQueue({ rerender = false, withLoadingSpinner = false, ...params } = {}) {
      DashboardTicker.stopTicker();
      DashboardTicker.loadTicker({
        req: () => this.getDashboardItems(params),
        preloadCallsStrategy: [
          [3000, 3],
          [5000, 5],
        ],
      });

      await this.getDashboardItems({ rerender, withLoadingSpinner, ...params });
    },

    async initDashboard({ suffix = this.suffix }) {
      this.dashboardItemsLoading = true;

      this.resetDashboardOptions();
      this.getMachineCategories({ suffix });
    },

    showJobs({ state, card }) {
      this.$refs.jobsViewer.$_openDialog({
        // @TODO move filters content checker to api service
        groupFilter: this.profitFilters.length ? this.profitFilters : null,
        shippingPeriods: this.shippingPeriodsFilters.join(',') || null,
        isGroup: state === CARD_STATES.IN_GROUP,
        machineCategory: this.machineCategory,
        suffix: this.suffix,
        ...card,
      });
    },

    moveQueueItemToMachine({ machine, card }) {
      this.$VBlackerTheme.openLoadingSpinner();

      this.wrapToLoadingFn({
        req: DashboardApiService.moveQueueItemToMachine.bind({}, {
          oldMachine: card.machine,
          targetMachine: machine,
          criterionId: card.id,
          suffix: this.suffix,
        }),
        closeLoadingSpinnerOnError: true,
        onSuccess: async () => {
          try {
            await this.refreshDashboardQueue();
          } finally {
            this.$VBlackerTheme.closeLoadingSpinner();
          }
        },
      });
    },

    moveAllQueueToMachine({ current, next }) {
      this.$VBlackerTheme.openLoadingSpinner();

      this.wrapToLoadingFn({
        req: DashboardApiService.moveAllQueueToMachine.bind({}, {
          suffix: this.suffix,
          oldMachine: current,
          targetMachine: next,
        }),
        closeLoadingSpinnerOnError: true,
        onSuccess: async () => {
          try {
            await this.refreshDashboardQueue();
          } finally {
            this.$VBlackerTheme.closeLoadingSpinner();
          }
        },
      });
    },

    showSendToQueuesDialog({ machine, card }) {
      Object.assign(this.toQueueSenderData, {
        item: {
          operation: card.operation,
          substrate: card.substrate,
          height: card.height,
          width: card.width,
        },
        filters: {
          groupFilter: this.profitFilters.length ? this.profitFilters : null,
          shippingPeriods: this.shippingPeriodsFilters.join(',') || null,
        },
        substrate: generateReplacementStringFromObj({
          name: card.substrate,
          height: card.height,
          width: card.width,
        }),
        minWidth: card.printingAreaWidth ?? 0,
        minHeight: card.printingAreaHeight ?? 0,
        machine,
      });

      this.toQueueSenderDialog = true;
    },

    async onSendToQueueSuccess() {
      try {
        await this.refreshDashboardQueue();
      } finally {
        this.$VBlackerTheme.closeLoadingSpinner();
      }
    },

    sendGroupItemToQueues({ machine, card }) {
      this.$VBlackerTheme.openLoadingSpinner();

      this.wrapToLoadingFn({
        req: DashboardApiService.sendGroupItemToMachine.bind({}, {
          groupFilter: this.profitFilters.length ? this.profitFilters : null,
          shippingPeriods: this.shippingPeriodsFilters.join(',') || null,
          substrateReplacement: null,
          operation: card.operation,
          substrate: card.substrate,
          height: card.height,
          suffix: this.suffix,
          width: card.width,
          machine,
        }),
        closeLoadingSpinnerOnError: true,
        onSuccess: async () => {
          try {
            await this.refreshDashboardQueue();
          } finally {
            this.$VBlackerTheme.closeLoadingSpinner();
          }
        },
      });
    },

    returnQueueItemToGroup({ id }) {
      this.$VBlackerTheme.openLoadingSpinner();

      this.wrapToLoadingFn({
        req: DashboardApiService.returnQueueItemToGroup.bind({}, {
          suffix: this.suffix,
          queueItemId: id,
        }),
        onSuccess: async () => {
          try {
            await this.refreshDashboardQueue({ withLoadingSpinner: false });
          } finally {
            this.$VBlackerTheme.closeLoadingSpinner();
          }
        },
      });
    },

    moveQueueItemUp({ id, force }) {
      this.$VBlackerTheme.openLoadingSpinner();

      this.wrapToLoadingFn({
        req: DashboardApiService.moveQueueItemUp.bind({}, {
          suffix: this.suffix,
          queueItemId: id,
          force,
        }),
        closeLoadingSpinnerOnError: true,
        onSuccess: async () => {
          try {
            await this.refreshDashboardQueue();
          } finally {
            this.$VBlackerTheme.closeLoadingSpinner();
          }
        },
      });
    },

    moveQueueItemDown({ id, force }) {
      this.$VBlackerTheme.openLoadingSpinner();

      this.wrapToLoadingFn({
        req: DashboardApiService.moveQueueItemDown.bind({}, {
          suffix: this.suffix,
          queueItemId: id,
          force,
        }),
        closeLoadingSpinnerOnError: true,
        onSuccess: async () => {
          try {
            await this.refreshDashboardQueue();
          } finally {
            this.$VBlackerTheme.closeLoadingSpinner();
          }
        },
      });
    },

    cancelBasketInTray({ id }) {
      const confirmDigital = generateRandom2Digits();

      this.$VBlackerTheme.alert.warning({
        placeholder: `Please confirm to cancel basket by typing in '${confirmDigital}'`,
        label: `Typing in '${confirmDigital}'`,
        validator: v => Number(v) === confirmDigital,
        text: 'Cancel basket?',
        withInput: true,
        outlined: true,
      }, () => {
        this.$VBlackerTheme.openLoadingSpinner();

        this.wrapToLoadingFn({
          req: DashboardApiService.cancelBasket.bind({}, {
            suffix: this.suffix,
            basketId: id,
          }),
          onSuccess: async () => {
            try {
              await this.refreshDashboardQueue();
            } finally {
              this.$VBlackerTheme.closeLoadingSpinner();
            }
          },
        });
      });
    },

    beginBasket(params) {
      DashboardTicker.stopTicker();

      this.$VBlackerTheme.openLoadingSpinner();

      this.wrapToLoadingFn({
        req: DashboardApiService.beginBasket.bind({}, {
          ...params,
          suffix: this.suffix,
        }),
        closeLoadingSpinnerOnError: true,
        onSuccess: async () => {
          try {
            await this.refreshDashboardQueue();
          } finally {
            this.$VBlackerTheme.closeLoadingSpinner();
          }
        },
        onReject: ({ msg }) => {
          this.refreshDashboardQueue();
          this.$VBlackerTheme.notification.error(msg);
        },
      });
    },

    markBasketLoaded(params) {
      DashboardTicker.stopTicker();

      this.$VBlackerTheme.openLoadingSpinner();

      this.wrapToLoadingFn({
        req: DashboardApiService.markBasketLoaded.bind({}, {
          ...params,
          suffix: this.suffix,
        }),
        closeLoadingSpinnerOnError: true,
        onSuccess: async () => {
          try {
            await this.refreshDashboardQueue();
          } finally {
            this.$VBlackerTheme.closeLoadingSpinner();
          }
        },
        onReject: ({ msg }) => {
          this.refreshDashboardQueue();
          this.$VBlackerTheme.notification.error(msg);
        },
      });
    },

    markBasketUnloaded(params) {
      DashboardTicker.stopTicker();

      this.$VBlackerTheme.openLoadingSpinner();

      this.wrapToLoadingFn({
        req: DashboardApiService.markBasketUnloaded.bind({}, {
          ...params,
          suffix: this.suffix,
        }),
        closeLoadingSpinnerOnError: true,
        onSuccess: async () => {
          try {
            await this.refreshDashboardQueue({ withLoadingSpinner: false });
          } finally {
            this.$VBlackerTheme.closeLoadingSpinner();
          }
        },
        onReject: ({ msg }) => {
          this.refreshDashboardQueue({ withLoadingSpinner: false });
          this.$VBlackerTheme.notification.error(msg);
        },
      });
    },

    processBasket(params) {
      DashboardTicker.stopTicker();

      this.$VBlackerTheme.openLoadingSpinner();

      this.wrapToLoadingFn({
        req: DashboardApiService.processBasket.bind({}, {
          ...params,
          suffix: this.suffix,
        }),
        closeLoadingSpinnerOnError: true,
        onSuccess: async () => {
          try {
            await this.refreshDashboardQueue();
          } finally {
            this.$VBlackerTheme.closeLoadingSpinner();
            // eslint-disable-next-line no-underscore-dangle
            params._closeProcessDialog();
          }
        },
        onReject: ({ msg }) => {
          this.refreshDashboardQueue();
          this.$VBlackerTheme.notification.error(msg);
        },
      });
    },

    async highlightFoundJobs(ids) {
      this.$set(this, 'highlightedFoundJobsIds', ids);
      this.$VBlackerTheme.openLoadingSpinner();

      try {
        await this.refreshDashboardQueue({ withLoadingSpinner: false });
        this.scrollToFirstFoundCard();
      } finally {
        this.$VBlackerTheme.closeLoadingSpinner();
      }
    },

    onChangeShippingPeriodsFilters(shippingPeriodsFilters) {
      this.shippingPeriodsFilters = shippingPeriodsFilters;
      this.refreshDashboardQueue({ shippingPeriodsFilters, withLoadingSpinner: true });
    },

    onChangeProfitFilters(profitFilters) {
      this.profitFilters = profitFilters;
      this.refreshDashboardQueue({ profitFilters, withLoadingSpinner: true });
    },

    updateScrollContainerHeight() {
      this.scrollContainerHeight = window.innerHeight - 220;
    },

    scrollToFirstFoundCard() {
      const [container] = this.$refs[`dashboardScrollContainer-${this.machineCategory}`];
      const isContainerWithScroll = (container.scrollWidth > container.clientWidth)
        || (container.scrollHeight > container.clientHeight);

      if (isContainerWithScroll) {
        const highlightedElements = [
          ...container.getElementsByClassName('pb-dashboard-card-highlighted'),
        ];

        const [x, y] = highlightedElements.reduce((acc, { offsetLeft: _x, offsetTop: _y }) => {
          acc[0] = (acc[0] > 0 && acc[0] < _x) ? acc[0] : _x;
          acc[1] = (acc[1] > 0 && acc[1] < _y) ? acc[1] : _y;

          return acc;
        }, [0, 0]);

        container.scroll(x, y);
      }
    },

    clearDashboardMemoization() {
      DashboardInProgressMemo.clearMemo();
      DashboardNotStartedMemo.clearMemo();
      DashboardGroupsMemo.clearMemo();
    },

    destroyDashboardMemoization() {
      DashboardInProgressMemo.destroyMemo();
      DashboardNotStartedMemo.destroyMemo();
      DashboardGroupsMemo.destroyMemo();
    },

    resetGroups() {
      this.$set(this, 'groups', {});
    },

    resetQueues() {
      this.printMachines = [];
      this.$set(this, 'queues', { inProgress: {}, notStarted: {} });
    },

    resetDashboardOptions() {
      this.resetGroups();
      this.resetQueues();

      this.highlightedFoundJobsIds = [];
      this.shippingPeriodsFilters = [];
      this.profitFilters = [];
    },

    wrapToLoadingFn,
  },
};
